import { useState } from 'react';
import { App, Modal, Typography } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../api/client';
import { tokens } from '../../theme';
import { tierApi, tierKeys, type TierDefinition } from './api';
import { TierColorPicker } from './TierColorPicker';

interface TierColorsModalProps {
  version: TierDefinition;
  open: boolean;
  onClose: () => void;
}

/**
 * Đổi màu hạng ngay trên bản đã phát hành, không tạo phiên bản mới (`S4-BE-17`, PL chốt
 * 2026-09-29). Màu chỉ là hiển thị nên không cần khóa như ngưỡng hay quyền lợi — những trường đó
 * vẫn chỉ đổi được qua bản nháp.
 *
 * Mỗi hạng một hàng chọn màu, khóa sẵn màu hạng khác đang dùng như form bản nháp. Máy chủ kiểm lại
 * cùng luật (bảng màu, không trùng màu).
 */
export function TierColorsModal({ version, open, onClose }: TierColorsModalProps) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [colors, setColors] = useState<Record<string, string>>(() =>
    Object.fromEntries(version.levels.map((l) => [l.code, l.colorHex ?? ''])),
  );

  const changed = version.levels.filter((l) => (l.colorHex ?? '') !== colors[l.code]);

  const mutation = useMutation({
    mutationFn: () =>
      tierApi.updateColors(
        version.id,
        Object.fromEntries(changed.map((l) => [l.code, colors[l.code] || null])),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tierKeys.all });
      message.success('Đã đổi màu hạng.');
      onClose();
    },
    onError: (err: unknown) => {
      message.error(err instanceof ApiError ? err.message : 'Không đổi được màu hạng.');
    },
  });

  /** Màu mà các hạng KHÁC đang chọn trong hộp thoại → tên hạng đó. */
  const takenBy = (code: string) =>
    new Map(
      version.levels
        .filter((l) => l.code !== code && !!colors[l.code])
        .map((l) => [colors[l.code].toLowerCase(), l.name || l.code] as const),
    );

  return (
    <Modal
      open={open}
      title={`Sửa màu hạng · bản ${version.version}`}
      okText="Lưu màu"
      cancelText="Hủy"
      width={640}
      confirmLoading={mutation.isPending}
      okButtonProps={{ disabled: changed.length === 0 }}
      onOk={() => mutation.mutate()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Chỉ đổi màu, không tạo phiên bản mới. Ngưỡng, tên và quyền lợi giữ nguyên.
      </Typography.Paragraph>
      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space.sectionGap / 2 }}>
        {version.levels.map((level) => (
          <div key={level.code} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Typography.Text strong style={{ width: 96, flex: 'none' }}>
              {level.name}
            </Typography.Text>
            <TierColorPicker
              value={colors[level.code] || null}
              onChange={(hex) => setColors((c) => ({ ...c, [level.code]: hex }))}
              colors={version.allowedColors}
              takenBy={takenBy(level.code)}
              levelName={level.name}
              disabled={mutation.isPending}
            />
          </div>
        ))}
      </div>
    </Modal>
  );
}
